import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCycleCounts, createCycleCount, getCycleCountDetail, startCycleCount, verifyCycleCountSlot, resolveCycleCountDiscrepancy, completeCycleCount } from '@/lib/api';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardCheck, Plus, Check, X, AlertTriangle, HelpCircle, MapPin } from 'lucide-react';

export default function CycleCountPage() {
  const { countId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ scope: 'full', scope_value: '', scheduled_date: new Date().toISOString().split('T')[0], frequency: 'one-time' });
  const [currentSlotIdx, setCurrentSlotIdx] = useState(0);
  const [verifyNotes, setVerifyNotes] = useState('');

  useEffect(() => {
    if (countId) loadDetail(countId);
    else loadCounts();
  }, [countId]);

  const loadCounts = async () => {
    try { const res = await getCycleCounts(); setCounts(res.data); }
    catch (err) { console.error(err); }
  };

  const loadDetail = async (id) => {
    try { const res = await getCycleCountDetail(id); setDetail(res.data); }
    catch (err) { toast.error('Not found'); navigate('/cycle-count'); }
  };

  const handleCreate = async () => {
    try {
      const res = await createCycleCount(form);
      toast.success(`Cycle count created: ${res.data.total_slots} slots`);
      setShowCreate(false);
      loadCounts();
    } catch (err) { toast.error('Failed'); }
  };

  const handleStart = async (id) => {
    try {
      await startCycleCount(id);
      toast.success('Cycle count started');
      navigate(`/cycle-count/${id}`);
    } catch (err) { toast.error('Failed'); }
  };

  const handleVerify = async (result) => {
    if (!detail) return;
    const slot = detail.slots[currentSlotIdx];
    if (!slot) return;
    const pallets = detail.pallets_by_slot?.[slot.slot_address] || [];

    try {
      await verifyCycleCountSlot(detail.id, {
        slot_address: slot.slot_address,
        result,
        expected_pallet_ref: pallets[0]?.pallet_ref || null,
        notes: verifyNotes,
      });

      setVerifyNotes('');
      if (currentSlotIdx < detail.slots.length - 1) {
        setCurrentSlotIdx(prev => prev + 1);
      }
      toast.success(`${slot.slot_address}: ${result}`);
      loadDetail(detail.id);
    } catch (err) { toast.error('Failed'); }
  };

  const handleComplete = async () => {
    if (!detail) return;
    try {
      await completeCycleCount(detail.id);
      toast.success('Cycle count completed');
      navigate('/cycle-count');
    } catch (err) { toast.error('Failed'); }
  };

  // Cycle count detail (execution) view
  if (detail) {
    const verified = new Set(detail.results?.map(r => r.slot_address) || []);
    const discrepancies = detail.results?.filter(r => r.result !== 'match') || [];
    const currentSlot = detail.slots?.[currentSlotIdx];
    const currentPallets = currentSlot ? (detail.pallets_by_slot?.[currentSlot.slot_address] || []) : [];
    const progress = detail.slots ? Math.round((verified.size / detail.slots.length) * 100) : 0;

    return (
      <div className="space-y-4 max-w-2xl mx-auto" data-testid="cycle-count-detail">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Cycle Count</h1>
          <Badge variant="outline">{detail.status} · {progress}%</Badge>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-[#1a2fce] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="text-xs text-gray-500">{verified.size} / {detail.slots?.length || 0} slots verified · {discrepancies.length} discrepancies</div>

        {/* Current Slot */}
        {detail.status === 'in_progress' && currentSlot && !verified.has(currentSlot.slot_address) && (
          <Card className="border-0 shadow-sm border-l-4 border-l-[#1a2fce]">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500 mb-1">Verify Slot #{currentSlotIdx + 1}</div>
              <div className="text-2xl font-mono font-bold text-[#0d1f6e] mb-2">{currentSlot.slot_address}</div>

              {currentPallets.length > 0 ? (
                <div className="mb-3">
                  <div className="text-xs text-gray-500 mb-1">Expected pallet(s):</div>
                  {currentPallets.map((p) => (
                    <div key={p.pallet_ref || p.id} className="text-sm font-mono bg-gray-50 rounded px-2 py-1 mb-1">{p.pallet_ref} — {p.status}</div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400 mb-3">Expected: Empty slot</div>
              )}

              <Input value={verifyNotes} onChange={e => setVerifyNotes(e.target.value)} placeholder="Notes (optional)" className="h-8 text-xs mb-3" />

              {/* Four-button result */}
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => handleVerify('match')} className="h-12 text-sm font-semibold" style={{ backgroundColor: '#0e7a45' }} data-testid="verify-match">
                  <Check size={16} className="mr-1" /> Match
                </Button>
                <Button onClick={() => handleVerify('mismatch_extra')} className="h-12 text-sm font-semibold bg-amber-500" data-testid="verify-extra">
                  <AlertTriangle size={16} className="mr-1" /> Extra Found
                </Button>
                <Button onClick={() => handleVerify('mismatch_missing')} className="h-12 text-sm font-semibold bg-red-500" data-testid="verify-missing">
                  <X size={16} className="mr-1" /> Missing
                </Button>
                <Button onClick={() => handleVerify('mismatch_wrong_item')} className="h-12 text-sm font-semibold bg-purple-600" data-testid="verify-wrong">
                  <HelpCircle size={16} className="mr-1" /> Wrong Item
                </Button>
              </div>

              {/* Skip to next/prev */}
              <div className="flex gap-2 mt-2">
                <Button onClick={() => setCurrentSlotIdx(Math.max(0, currentSlotIdx - 1))} disabled={currentSlotIdx === 0} variant="outline" className="flex-1 h-8 text-xs">Previous</Button>
                <Button onClick={() => setCurrentSlotIdx(Math.min(detail.slots.length - 1, currentSlotIdx + 1))} variant="outline" className="flex-1 h-8 text-xs">Skip</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Discrepancies */}
        {discrepancies.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-red-600">Discrepancies ({discrepancies.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {discrepancies.map((d) => (
                <div key={d.slot_address || d.id} className="p-2 border rounded-lg flex items-center gap-2">
                  <MapPin size={14} className="text-red-500" />
                  <div className="flex-1">
                    <div className="font-mono text-sm font-bold">{d.slot_address}</div>
                    <div className="text-xs text-gray-500">{d.result?.replace(/_/g, ' ')} {d.notes && `— ${d.notes}`}</div>
                  </div>
                  {d.resolution ? (
                    <Badge className="text-[10px] bg-green-600">Resolved</Badge>
                  ) : ['manager', 'admin'].includes(user?.role) ? (
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={async () => {
                      const notes = prompt('Resolution notes:');
                      if (notes !== null) {
                        await resolveCycleCountDiscrepancy(detail.id, { slot_address: d.slot_address, resolution: 'adjusted', notes });
                        toast.success('Resolved');
                        loadDetail(detail.id);
                      }
                    }}>Resolve</Button>
                  ) : <Badge variant="secondary" className="text-[10px]">Pending</Badge>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Complete */}
        {['manager', 'admin'].includes(user?.role) && detail.status === 'in_progress' && (
          <Button onClick={handleComplete} className="w-full h-11" style={{ backgroundColor: '#0e7a45' }} data-testid="complete-count">
            <Check size={14} className="mr-1" /> Complete Cycle Count
          </Button>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4" data-testid="cycle-count-list">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><ClipboardCheck size={20} /> Cycle Counts</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="h-9 text-xs" style={{ backgroundColor: '#1a2fce' }} data-testid="new-cycle-count">
              <Plus size={14} className="mr-1" /> New Count
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Schedule Cycle Count</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={form.scope} onValueChange={v => setForm(p => ({...p, scope: v}))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Warehouse</SelectItem>
                    <SelectItem value="row">Single Row</SelectItem>
                    <SelectItem value="rack">Single Rack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.scope !== 'full' && (
                <div>
                  <Label className="text-xs">{form.scope === 'row' ? 'Row (A/B/C/D)' : 'Rack Code (e.g. A05)'}</Label>
                  <Input value={form.scope_value} onChange={e => setForm(p => ({...p, scope_value: e.target.value}))} className="h-9 text-sm" />
                </div>
              )}
              <div>
                <Label className="text-xs">Scheduled Date</Label>
                <Input type="date" value={form.scheduled_date} onChange={e => setForm(p => ({...p, scheduled_date: e.target.value}))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Frequency</Label>
                <Select value={form.frequency} onValueChange={v => setForm(p => ({...p, frequency: v}))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }}>Create Cycle Count</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {counts.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ClipboardCheck size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No cycle counts scheduled</p>
        </div>
      ) : (
        <div className="space-y-2">
          {counts.map(cc => (
            <Card key={cc.id} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold capitalize">{cc.scope} Count</span>
                    {cc.scope_value && <Badge variant="outline" className="text-xs">{cc.scope_value}</Badge>}
                    <Badge variant={cc.status === 'completed' ? 'default' : cc.status === 'in_progress' ? 'secondary' : 'outline'} className="text-xs">{cc.status}</Badge>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {cc.total_slots} slots · {cc.verified_count || 0} verified · {cc.discrepancy_count || 0} discrepancies
                  </div>
                  <div className="text-xs text-gray-400">{cc.scheduled_date} · {cc.frequency}</div>
                </div>
                {cc.status === 'scheduled' && (
                  <Button onClick={() => handleStart(cc.id)} variant="outline" size="sm" className="text-xs h-7">Start</Button>
                )}
                {cc.status === 'in_progress' && (
                  <Button onClick={() => navigate(`/cycle-count/${cc.id}`)} size="sm" className="text-xs h-7" style={{ backgroundColor: '#1a2fce' }}>Continue</Button>
                )}
                {cc.status === 'completed' && (
                  <Button onClick={() => navigate(`/cycle-count/${cc.id}`)} variant="outline" size="sm" className="text-xs h-7">View</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
